import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Dropdown, Nav, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getOverageItemsAPI, resolveOverageAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';

const ACTIONS = [
  { key: 'approve_temp', label: 'Approve Temporary Increase' },
  { key: 'notify_billing', label: 'Notify Billing' },
  { key: 'throttle', label: 'Throttle' },
  { key: 'ignore_7d', label: 'Ignore 7 days' },
];

function suggestedAction(row) {
  return row.overagePct > 20 ? 'Upgrade Plan' : 'Temporary Increase';
}

function metricLabel(r) {
  return String(r.resource || '').replace(/_/g, ' ');
}

export default function OverageReview() {
  const [openRows, setOpenRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [selected, setSelected] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOverageItemsAPI();
      setOpenRows(res.data.open || []);
      setHistoryRows(res.data.history || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (row, actionKey) => {
    try {
      await resolveOverageAPI(row.id, actionKey);
      toast.success('Overage updated');
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openColumns = useMemo(
    () => [
      {
        name: 'Tenant',
        selector: (r) => r.companyName,
        sortable: true,
        grow: 2,
      },
      {
        name: 'Metric',
        selector: (r) => metricLabel(r),
        sortable: true,
      },
      {
        name: 'Over by',
        selector: (r) => `${r.overAmount} (${r.overagePct}%)`,
        sortable: true,
      },
      {
        id: 'since',
        name: 'Since',
        selector: (r) => r.since,
        sortable: true,
        sortFunction: (a, b) => new Date(a.since) - new Date(b.since),
        format: (r) => new Date(r.since).toLocaleDateString(),
      },
      {
        name: 'Suggested action',
        selector: (r) => suggestedAction(r),
        grow: 1,
      },
      {
        name: 'Owner',
        selector: (r) => r.owner,
        sortable: true,
      },
      {
        name: 'Actions',
        cell: (r) => (
          <Dropdown>
            <Dropdown.Toggle size="sm" variant="outline-secondary" id={`act-${r.id}`}>
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {ACTIONS.map((a) => (
                <Dropdown.Item key={a.key} onClick={() => setConfirm({ row: r, action: a })}>
                  {a.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        ),
        ignoreRowClick: true,
      },
    ],
    []
  );

  const historyColumns = useMemo(
    () => [
      {
        name: 'Tenant',
        selector: (r) => r.companyName,
        sortable: true,
      },
      {
        name: 'Metric',
        selector: (r) => metricLabel(r),
        sortable: true,
      },
      {
        id: 'resolvedAt',
        name: 'Resolved',
        selector: (r) => r.resolvedAt,
        sortable: true,
        sortFunction: (a, b) => new Date(a.resolvedAt) - new Date(b.resolvedAt),
        format: (r) => new Date(r.resolvedAt).toLocaleString(),
      },
      {
        name: 'Action',
        selector: (r) => r.action,
        grow: 2,
      },
    ],
    []
  );

  const bulkNotify = () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) {
      toast.info('Select at least one row');
      return;
    }
    toast.success(`Bulk notify queued for ${ids.length} tenant(s) (simulated)`);
  };

  if (loading && !openRows.length && !historyRows.length) {
    return (
      <div>
        <PageHeader
          title="Overage review"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Usage', path: platformUrl('usage') },
            { label: 'Overage' },
          ]}
        />
        <LoadingSkeleton rows={6} cols={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Overage review"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Usage', path: platformUrl('usage') },
            { label: 'Overage' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load overages"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Overage review"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Usage', path: platformUrl('usage') },
          { label: 'Overage' },
        ]}
      />

      <Tab.Container defaultActiveKey="open">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="open">Open</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="history">History</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="open">
            <DataTableWrapper
              columns={openColumns}
              data={openRows}
              loading={false}
              keyField="id"
              selectableRows
              onSelectedRowsChange={({ selectedRows: sr }) => {
                const m = {};
                (sr || []).forEach((r) => {
                  m[r.id] = true;
                });
                setSelected(m);
              }}
              actions={[{ label: 'Bulk notify', onClick: bulkNotify }]}
              customTableProps={{ defaultSortFieldId: 'since' }}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="history">
            <DataTableWrapper
              columns={historyColumns}
              data={historyRows}
              loading={false}
              keyField="id"
              showSearch={false}
              customTableProps={{ defaultSortFieldId: 'resolvedAt' }}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <ConfirmModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => runAction(confirm.row, confirm.action.key)}
        title={confirm?.action?.label}
        message={`Apply “${confirm?.action?.label}” to ${confirm?.row?.companyName}?`}
        confirmLabel="Confirm"
      />
    </div>
  );
}
